import React, { useEffect } from "react";
import {
  fetchReportsData,
  type CtoReportsRequest,
  type CtoLhcSummary,
  type CtoReportsPage,
  CtoLhcTotals,
} from "./api/reports";

import { DataTable } from "../../components/ui/data-table";
import { useBreadcrumbs } from "../../contexts/BreadcrumbsContext";
import { createSimConsentReportsColumns } from "./SimConsentReportColumn";
import { PaginationState } from "@tanstack/react-table";
import { Search } from "lucide-react";
import { Input } from "../../components/ui/input";
import { cn } from "lib/utils";
import { Alert, AlertDescription } from "components/ui/alert";

const StatCardSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border py-6 w-full">
      <div className="h-3 w-20 bg-gray-200 animate-pulse rounded mb-2"></div>
      <div className="h-3 w-12 bg-gray-300 animate-pulse rounded"></div>
    </div>
  );
};

const SimConsentStatCard: React.FC<{
  title: string;
  value: string | number | undefined;
  color?: "blue" | "green" | "red" | "neutral";
}> = ({ title, value, color = "blue" }) => {
  const cardStyles: Record<string, { 
    border: string; 
    gradient: string; 
    text: string;
  }> = {
    blue: {
      border: "border-blue-200",
      gradient: "bg-gradient-to-br from-blue-50 via-white to-white",
      text: "text-blue-700",
    },
    green: {
      border: "border-green-200",
      gradient: "bg-gradient-to-br from-green-50 via-white to-white",
      text: "text-green-700",
    },
    red: {
      border: "border-red-200",
      gradient: "bg-gradient-to-br from-red-50 via-white to-white",
      text: "text-red-700",
    },
    neutral: {
      border: "border-slate-200",
      gradient: "bg-gradient-to-br from-slate-50 via-white to-white",
      text: "text-slate-700",
    }
  };

  const style = cardStyles[color];

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-xl border py-4 w-full cursor-pointer transition-all duration-200",
        style.border,
        style.gradient,
        "shadow-sm",
        "hover:shadow-[0_4px_6px_rgba(0,0,0,0.12)] hover:-translate-y-[1px]"
      )}
    >
      <div className="text-xs font-medium text-gray-500">{title}</div>
      <div className={cn("text-xl font-semibold mt-1", style.text)}>{value}</div>
    </div>
  );
};

const SimConsentReportPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([{label: "Reports", href: "/reports"}, 
        {label: "Sim Consent Report"}
    ]);
  }, [setBreadcrumbs]);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");
  const [pageCount, setPageCount] = React.useState(0);
  const [totalItems, setTotalItems] = React.useState(0);
  const [stats, setStats] = React.useState<CtoLhcTotals>();
  const [searchText, setSearchText] = React.useState<string | null>(null);
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  const [reportData, setReportData] = React.useState<CtoLhcSummary[]>([]);

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [sorting, setSorting] = React.useState<{ id: string; desc: boolean }[]>(
    [{ id: "allowed_pct", desc: true }]
  );

  const columns = createSimConsentReportsColumns();

  type ReportColumnKeys =
    | "allowed_pct"
    | "lhc_branch"
    | "total_lhcs"
    | "allowed_count"
    | "null_count";

  const fetchReportData = React.useCallback(async () => {
    try {
      setLoading(true);
      const sortId = sorting[0]?.id as ReportColumnKeys | undefined;

      const payload: CtoReportsRequest = {
        sort_by: sortId || "allowed_pct",
        sort_order: sorting[0]?.desc ? "desc" : "asc",
        page: pagination.pageIndex + 1, // backend pages start at 1
        page_size: pagination.pageSize,
        search: debouncedSearch || undefined,
      };

      const res: CtoReportsPage = await fetchReportsData(payload);
      setReportData(res.items ?? []);
      setTotalItems(res.total_items ?? 0);
      setPageCount(res.total_pages ?? 0);
      setStats(res.total ?? {});
      setError("");
    } catch (err: any) {
      console.error("Failed to fetch report data", err);
      setReportData([]);
      setTotalItems(0);
      setError(err?.response?.data?.detail || err?.message || "Failed to fetch report data");
    } finally {
        setLoading(false);
    }
  }, [sorting, pagination, debouncedSearch]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  useEffect(() => {
    if(searchText == null) return ;
    const timer = setTimeout(() => {
        setDebouncedSearch(searchText ?? "");
        setPagination((old) => ({ ...old, pageIndex: 0 }));
    }, 700);

    return () => clearTimeout(timer);
  }, [searchText]);

  const handleSortingChange = (updater: any) => {
    setSorting(updater);
  };

  const handlePaginationChange = (updaterOrValue: PaginationState | ((old: PaginationState) => PaginationState)) => {
    setPagination((old) => {
      if (typeof updaterOrValue === "function") {
        return (updaterOrValue as (old: PaginationState) => PaginationState)(old);
      }
      return updaterOrValue;
    });
  };

  const statCards = [
    { title: "Total Line LHC's", value: stats?.total_lhcs, color: "blue" as const },
    { title: "Consent Taken", value: stats?.total_allowed_count, color: "green" as const },
    { title: "Consent Not Taken", value: stats?.total_null_count, color: "red" as const },
    { title: "Consent Taken %", value: `${stats?.total_allowed_pct}%`, color: "green" as const },
  ];

  return (
    <div className="space-y-6">
      <div className="flex gap-4 px-3 mt-4">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
          : statCards.map((item) => (
              <SimConsentStatCard
                key={item.title}
                title={item.title}
                value={item.value}
                color={item.color}
              />
          ))}
      </div>

      <div className="relative px-3">
        <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
            placeholder="Search LHC Branch..."
            value={searchText ?? ""}
            onChange={(e) => setSearchText(e.target.value)}
            className="h-8 w-[150px] lg:w-[250px] pl-8 text-xs font-normal text-gray-600 placeholder:text-gray-500"
        />
      </div>

      {!loading && error && (
        <div className="px-3 mt-3">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      <div className="mt-6">
        <DataTable
          columns={columns}
          data={reportData}
          loading={loading}
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          hideToolbar={true}
          totalCount={totalItems}
          pageCount={pageCount}
        />
      </div>
    </div>
  );
};

export default SimConsentReportPage;
